import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const WIDTH = 800;
const ROW_HEIGHT = 35;
const OVERLAP_FACTOR = 0.4;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 80 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function kernelDensityEstimator(kernel, X) {
  return function (V) {
    return X.map((x) => [x, d3.mean(V, (v) => kernel(x - v))]);
  };
}

function kernelEpanechnikov(k) {
  return function (v) {
    return Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0;
  };
}

export default function RidgelinePlot({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.distributions) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { dates, distributions } = data;

    // Sort dates chronologically (oldest first = bottom, newest last = top)
    const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));

    const numDates = sortedDates.length;
    const effectiveRowHeight = ROW_HEIGHT * (1 - OVERLAP_FACTOR);
    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const totalHeight = MARGIN.top + numDates * effectiveRowHeight + ROW_HEIGHT + MARGIN.bottom;

    svg.attr('width', WIDTH).attr('height', totalHeight);

    // Collect all values to determine shared x domain
    const allValues = [];
    sortedDates.forEach((date) => {
      const vals = distributions[date];
      if (vals && vals.length > 0) {
        vals.forEach((v) => allValues.push(v));
      }
    });

    if (allValues.length === 0) return;

    const xExtent = d3.extent(allValues);
    const xPadding = (xExtent[1] - xExtent[0]) * 0.1 || 5;
    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPadding, xExtent[1] + xPadding])
      .range([0, innerW]);

    // KDE sampling points
    const ticks = x.ticks(100);
    const bandwidth = (xExtent[1] - xExtent[0]) / 8 || 2;
    const kde = kernelDensityEstimator(kernelEpanechnikov(bandwidth), ticks);

    // Compute densities for all dates
    const densities = {};
    let maxDensity = 0;
    sortedDates.forEach((date) => {
      const vals = distributions[date];
      if (vals && vals.length > 0) {
        const density = kde(vals);
        densities[date] = density;
        const localMax = d3.max(density, (d) => d[1]);
        if (localMax > maxDensity) maxDensity = localMax;
      }
    });

    // Y scale for density within each row
    const yDensity = d3.scaleLinear()
      .domain([0, maxDensity])
      .range([0, ROW_HEIGHT]);

    // Color gradient: oldest (#93c5fd) to newest (#7c3aed)
    const colorScale = d3.scaleLinear()
      .domain([0, numDates - 1])
      .range(['#93c5fd', '#7c3aed']);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Area generator
    const area = d3.area()
      .curve(d3.curveBasis)
      .x((d) => x(d[0]))
      .y0(0)
      .y1((d) => -yDensity(d[1]));

    const line = d3.line()
      .curve(d3.curveBasis)
      .x((d) => x(d[0]))
      .y((d) => -yDensity(d[1]));

    // Draw rows bottom-to-top: index 0 = oldest = bottom
    sortedDates.forEach((date, i) => {
      const density = densities[date];
      if (!density) return;

      // Y position: oldest at bottom, newest at top
      const rowY = (numDates - 1 - i) * effectiveRowHeight + ROW_HEIGHT;
      const color = colorScale(i);

      const rowG = g.append('g').attr('transform', `translate(0, ${rowY})`);

      // Filled area
      rowG.append('path')
        .datum(density)
        .attr('d', area)
        .attr('fill', color)
        .attr('fill-opacity', 0.6)
        .attr('stroke', 'none');

      // Outline
      rowG.append('path')
        .datum(density)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.9);

      // Invisible hover rect for the entire row
      const vals = distributions[date] || [];
      const mean = vals.length > 0 ? d3.mean(vals) : 0;
      const sorted = [...vals].sort(d3.ascending);
      const median = vals.length > 0 ? d3.median(sorted) : 0;

      rowG.append('rect')
        .attr('x', 0)
        .attr('y', -ROW_HEIGHT)
        .attr('width', innerW)
        .attr('height', ROW_HEIGHT)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: date,
            count: vals.length,
            mean: mean,
            median: median,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Date label on the left
      const dateObj = new Date(date);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `${months[dateObj.getMonth()]} ${dateObj.getDate()}`;

      g.append('text')
        .attr('x', -10)
        .attr('y', rowY)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(label);
    });

    // X axis at the bottom
    const xAxisY = numDates * effectiveRowHeight + ROW_HEIGHT;
    g.append('g')
      .attr('transform', `translate(0, ${xAxisY})`)
      .call(d3.axisBottom(x).ticks(8).tickFormat((v) => `$${v}`))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.dates || data.dates.length === 0 || !data.distributions) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Distribution Over Time (Ridgeline)</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No distribution data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Distribution Over Time (Ridgeline)</div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.date}</div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            Samples: {tooltip.count}
          </div>
          <div style={{ color: '#2563eb', fontWeight: 600 }}>
            Mean: ${tooltip.mean.toFixed(2)}
          </div>
          <div style={{ color: '#7c3aed', fontWeight: 600 }}>
            Median: ${tooltip.median.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}
